import { ReactNode, useEffect, useRef } from "react";
import { UserMenuProps } from "./or-header.types";
import { cva, cx } from "class-variance-authority";
import { AtButton, AtButtonSize, AtButtonType } from "../../atoms";

const mlMenuDropdownVariant = cva("", {
  variants: {
    visibility: { true: "block", false: "hidden" },
  },
  defaultVariants: {},
});

export const UserMenu: React.FC<UserMenuProps> = ({
  isOpen = false,
  "data-testid": testId,
  userInfo,
  logoutLabel,
  children,
  onCloseMenu,
  onClick,
  onMouseEnter,
  onLogout,
}: UserMenuProps) => {
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bg = bgRef.current;
    if (!bg || !onCloseMenu) return;

    bg.addEventListener("mouseover", onCloseMenu);
    bg.addEventListener("click", onCloseMenu);

    return () => {
      bg.removeEventListener("mouseover", onCloseMenu);
      bg.removeEventListener("click", onCloseMenu);
    };
  }, [onCloseMenu]);

  const handleOnClick = () => {
    onClick?.(isOpen);
  };

  return (
    <div className={"relative"} data-testid={testId} onMouseLeave={onCloseMenu}>
      {/* Main button */}
      <AtButton
        text={userInfo.name}
        data-testid={`${testId}-button`}
        onClick={handleOnClick}
        onFocus={onMouseEnter}
        onMouseEnter={onMouseEnter}
        startIcon={"support-agent-filled"}
        onTouchStart={onMouseEnter}
        size={AtButtonSize.MEDIUM}
        className="px-4!"
      />

      {children && (
        <>
          <div className="absolute w-60 left-1/2 -translate-x-1/2 z-30">
            {/* Menu dropdown */}
            <BlockMenu
              logoutLabel={logoutLabel}
              isOpen={isOpen}
              testId={testId}
              onLogout={onLogout}
            >
              {children}
            </BlockMenu>
          </div>
          {isOpen ? (
            /* Opacity background */
            <div
              className={cx(
                "fixed w-screen h-screen left-0 bg-opacity-black z-20",
                mlMenuDropdownVariant(),
              )}
              ref={bgRef}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

interface BlockMenuProps {
  logoutLabel?: string;
  isOpen?: boolean;
  testId?: string;
  children?: ReactNode;
  onLogout?: () => void;
}

const BlockMenu = ({
  isOpen = false,
  testId,
  logoutLabel,
  children,
  onLogout,
}: BlockMenuProps) => {
  return (
    <div
      className={cx(
        "relative",
        'before:content-[""] before:absolute before:-top-5 before:left-1/2 before:-translate-x-1/2 before:border-[10.5px] before:border-solid before:border-[transparent_transparent_#fff_transparent]',
        "bg-white mt-2.5 rounded-sm shadow p-[1.125rem] flex flex-col gap-4 text-center text-surface-500",
        mlMenuDropdownVariant({ visibility: isOpen }),
      )}
    >
      {children}
      {logoutLabel && (
        <AtButton
          data-testid={`${testId}-button-logout`}
          text={logoutLabel}
          buttonType={AtButtonType.SECONDARY}
          size={AtButtonSize.EXTRA_SMALL}
          onClick={onLogout}
        />
      )}
    </div>
  );
};
